import React, { useCallback, useState } from "react";
import styled from "styled-components";

import Modal from "../../../components/modal/Modal";
import Button from "../../../components/button/Button";
import Textarea from "../../../components/form-item/Textarea";
import Autofill from "../../../components/autofill/AutoFill";
import {
  useSubmitDmReviewMutation,
  type DmReviewEntry,
  type DmReviewRatingInput,
  type DmReviewStatus,
} from "../../../app/services/dmThreads";
import { useTransformTextMutation } from "../../../app/services/aiText";
import { reviewRatingIcon, reviewRatingLabel } from "./reviewRating";

const NOTE_MAX_LENGTH = 1500;

const CONSENT_OPTIONS: {
  value: DmReviewStatus;
  label: string;
  description: string;
}[] = [
  {
    value: "OK",
    label: "OK",
    description: "投稿者名つきでプロフィールに掲載される場合があります。",
  },
  {
    value: "匿名ならOK",
    label: "匿名ならOK",
    description: "掲載時は投稿者名を伏せます。",
  },
  {
    value: "許可しない",
    label: "許可しない",
    description: "プロフィールには掲載されません。",
  },
];

// ── styled（選択状態を持つトグルは局所コンポーネントで表現） ──

const RatingButton = styled.button<{ $active: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
  padding: 12px;
  border-radius: 8px;
  background-color: #fff;
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--soremo-blue)" : "var(--soremo-border)"};
  outline: ${({ $active }) =>
    $active ? "1px solid var(--soremo-blue)" : "none"};
  color: ${({ $active }) =>
    $active ? "var(--soremo-blue)" : "var(--soremo-gray)"};
  cursor: pointer;
  transition: all 0.2s ease;

  .material-symbols-rounded {
    font-size: 24px;
  }

  @media (hover: hover) {
    &:hover {
      border-color: var(--soremo-blue);
    }
  }
`;

const ConsentOption = styled.button<{ $active: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  width: 100%;
  text-align: left;
  padding: 12px;
  border-radius: 8px;
  background-color: #fff;
  border: 1px solid
    ${({ $active }) =>
      $active ? "var(--soremo-blue)" : "var(--soremo-border)"};
  cursor: pointer;
  transition: all 0.2s ease;

  .material-symbols-rounded {
    font-size: 20px;
    color: ${({ $active }) =>
      $active ? "var(--soremo-blue)" : "var(--soremo-light-gray)"};
  }
`;

const PreviewBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px;
  border-radius: 8px;
  background-color: var(--soremo-background);
`;

const PreviewNote = styled.p.attrs({ className: "bodytext-13" })`
  white-space: pre-wrap;
  margin: 0;
  color: var(--soremo-gray);
`;

const HelpText = styled.span.attrs({ className: "bodytext-11" })`
  color: var(--soremo-light-gray);
`;

const ErrorText = styled.div.attrs({ className: "bodytext-13" })`
  color: var(--soremo-green);
`;

const resolveAiError = (err: unknown): string => {
  const status = (err as { status?: number })?.status;
  if (status === 503) {
    return "AI 機能が現在利用できません。少し時間をおいてお試しください。";
  }
  return "AI による下書き生成に失敗しました。時間をおいて再度お試しください。";
};

export interface OfferReviewModalProps {
  projectId: string;
  dmId: string;
  /** レビュー対象（相手）の表示名。タイトル表示に使う。 */
  revieweeName: string;
  /** 既存レビュー（再編集時の初期値）。なければ新規。 */
  existing?: DmReviewEntry | null;
  onClose: () => void;
  /** 投稿成功時に呼ばれる。 */
  onSubmitted?: () => void;
}

/**
 * オファー完了後の相互レビュー投稿モーダル（2 段階）。
 *
 * - step "edit": 評価（Good/Bad）+ テーマから AI 下書き + 本文入力
 * - step "consent": 本文プレビュー + プロフィール掲載の同意 → 送信
 *
 * AI 下書きはテーマ（任意）・評価・その DM のやり取り履歴を文脈に、
 * `review_assistant_offer` シーンで生成する（BE が履歴と相手名を解決）。
 */
const OfferReviewModal: React.FC<OfferReviewModalProps> = ({
  projectId,
  dmId,
  revieweeName,
  existing,
  onClose,
  onSubmitted,
}) => {
  const [step, setStep] = useState<"edit" | "consent">("edit");
  const [rating, setRating] = useState<DmReviewRatingInput | null>(
    existing?.rating === "LIKE" || existing?.rating === "DISLIKE"
      ? existing.rating
      : null,
  );
  const [note, setNote] = useState<string>(existing?.note ?? "");
  const [consent, setConsent] = useState<DmReviewStatus>(
    existing?.status ?? "OK",
  );
  const [selectedTheme, setSelectedTheme] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [transformText, { isLoading: isGenerating }] =
    useTransformTextMutation();
  const [submitReview, { isLoading: isSubmitting }] =
    useSubmitDmReviewMutation();

  const handleGenerate = useCallback(
    async (theme: string | null) => {
      if (!rating) {
        setErrorMessage("先に評価（良かった / 残念だった）を選んでください。");
        return;
      }
      setErrorMessage(null);
      try {
        const res = await transformText({
          scene: "review_assistant_offer",
          text: note,
          project_id: projectId,
          dm_id: dmId,
          theme: theme ?? undefined,
          rating,
        }).unwrap();
        const generated = res?.data?.transformed_text ?? "";
        if (generated) {
          setNote(generated.slice(0, NOTE_MAX_LENGTH));
        }
      } catch (err) {
        setErrorMessage(resolveAiError(err));
      }
    },
    [rating, note, projectId, dmId, transformText],
  );

  const handleThemeSelect = useCallback(
    (keyword: string) => {
      setSelectedTheme(keyword);
      void handleGenerate(keyword);
    },
    [handleGenerate],
  );

  const handleSubmit = useCallback(async () => {
    if (!rating || !note.trim()) return;
    setErrorMessage(null);
    try {
      await submitReview({
        project_id: projectId,
        dm_id: dmId,
        rating,
        note: note.trim(),
        status: consent,
      }).unwrap();
      onSubmitted?.();
      onClose();
    } catch {
      setErrorMessage(
        "レビューの送信に失敗しました。時間をおいて再度お試しください。",
      );
    }
  }, [rating, note, consent, projectId, dmId, submitReview, onSubmitted, onClose]);

  if (step === "edit") {
    return (
      <Modal
        onClose={onClose}
        title={`${revieweeName}さんへのレビュー`}
        description="このレビューは相手に共有されます。テーマを選ぶと AI が下書きを作成します。"
        size="default"
        primaryAction={{
          label: "次へ",
          onClick: () => {
            setErrorMessage(null);
            setStep("consent");
          },
          disabled: !rating || !note.trim() || isGenerating,
          rightIcon: "chevron_right",
        }}
        secondaryAction={{
          label: "キャンセル",
          onClick: onClose,
          theme: "secondary",
          variant: "outlined",
        }}
      >
        <div className="u-col u-gap8 u-w100">
          <span className="bodytext-13">評価</span>
          <div className="u-row u-gap8 u-w100">
            <RatingButton
              type="button"
              $active={rating === "LIKE"}
              onClick={() => setRating("LIKE")}
            >
              <span className="material-symbols-rounded">thumb_up</span>
              良かった
            </RatingButton>
            <RatingButton
              type="button"
              $active={rating === "DISLIKE"}
              onClick={() => setRating("DISLIKE")}
            >
              <span className="material-symbols-rounded">thumb_down</span>
              残念だった
            </RatingButton>
          </div>
        </div>

        <div className="u-col u-gap8 u-w100">
          <span className="bodytext-13">テーマから AI で下書き</span>
          {/* 折り返し表示（nowrap なし）で全テーマを表示する */}
          <Autofill group="review" onSelectKeyword={handleThemeSelect} />
          <HelpText>
            テーマをタップすると、やり取りの内容を踏まえた下書きを生成します。
          </HelpText>
        </div>

        <Textarea
          label="レビュー本文"
          option="required"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={6}
          maxLength={NOTE_MAX_LENGTH}
          placeholder="一緒に進めた感想を書きましょう。"
        />

        <div className="u-row u-gap8">
          <Button
            size="small"
            theme="secondary"
            variant="outlined"
            leftIcon="auto_awesome"
            onClick={() => handleGenerate(selectedTheme)}
            disabled={isGenerating || !rating}
          >
            {isGenerating ? "作成中..." : "AI で作成"}
          </Button>
        </div>

        {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
      </Modal>
    );
  }

  const ratingIcon = reviewRatingIcon(rating);
  const ratingLabel = reviewRatingLabel(rating);

  return (
    <Modal
      onClose={isSubmitting ? undefined : onClose}
      title="公開設定の確認"
      description="本文を確認し、プロフィールへの掲載可否を選んでください。"
      size="default"
      primaryAction={{
        label: isSubmitting ? "送信中..." : "送信",
        onClick: handleSubmit,
        disabled: isSubmitting,
        rightIcon: "send",
      }}
      secondaryAction={{
        label: "戻る",
        onClick: () => {
          setErrorMessage(null);
          setStep("edit");
        },
        disabled: isSubmitting,
      }}
    >
      <PreviewBlock>
        <div className="u-row u-gap4 u-items-center">
          {ratingIcon && (
            <span className="material-symbols-rounded">{ratingIcon}</span>
          )}
          <span className="bodytext-13">{ratingLabel}</span>
        </div>
        <PreviewNote>{note}</PreviewNote>
      </PreviewBlock>

      <div className="u-col u-gap8 u-w100">
        <span className="bodytext-13">プロフィールへの掲載</span>
        {CONSENT_OPTIONS.map((opt) => (
          <ConsentOption
            key={opt.value}
            type="button"
            $active={consent === opt.value}
            onClick={() => setConsent(opt.value)}
          >
            <span className="material-symbols-rounded">
              {consent === opt.value
                ? "radio_button_checked"
                : "radio_button_unchecked"}
            </span>
            <span className="u-col u-gap4">
              <span className="bodytext-13">{opt.label}</span>
              <HelpText>{opt.description}</HelpText>
            </span>
          </ConsentOption>
        ))}
      </div>

      {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
    </Modal>
  );
};

export default OfferReviewModal;
